import React, { useState, useEffect } from "react";
import { Search, Sun, Moon, Bell, Menu, ChevronDown, LogOut } from "lucide-react";
import useTheme from "../../components/aura/useTheme";
import { supabase } from "../../lib/supabase";

const TopBar = ({ title = "Today", subtitle, onOpenSidebar, onOpenPalette }) => {
  const { effective, toggle } = useTheme();
  const [openProfile, setOpenProfile] = useState(false);
  const [openNotif, setOpenNotif] = useState(false);
  
  const [profile, setProfile] = useState({ display_name: "", cefr_level: "A1", target_language: "" });

  useEffect(() => {
    async function loadProfile() {
      const { data: authData } = await supabase.auth.getUser();
      const uid = authData?.user?.id;
      if (!uid) return;
      const { data } = await supabase
        .from("users")
        .select("display_name, cefr_level, target_language")
        .eq("id", uid)
        .maybeSingle();
      if (data) setProfile(data);
    }
    loadProfile();
    
    // Listen for auth state changes
    const { data: sub } = supabase.auth.onAuthStateChange(() => loadProfile());
    
    // Listen for custom profile update events (from Settings)
    const handleProfileUpdate = () => loadProfile();
    window.addEventListener("aura_profile_updated", handleProfileUpdate);
    
    return () => {
      sub.subscription.unsubscribe();
      window.removeEventListener("aura_profile_updated", handleProfileUpdate);
    };
  }, []);

  const initial = profile.display_name?.[0]?.toUpperCase() || "?";
  const name = profile.display_name || "You";
  const lang = profile.target_language || "Language";
  const cefr = profile.cefr_level || "A1";

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    window.location.href = "/";
  };

  return (
    <header
      className="sticky top-0 z-30 border-b border-panel/6 bg-base/85 backdrop-blur-xl"
      data-testid="dashboard-topbar"
    >
      <div className="px-4 sm:px-6 h-16 flex items-center gap-3">
        <button
          onClick={onOpenSidebar}
          className="lg:hidden w-9 h-9 grid place-items-center rounded-lg border border-panel/10 hover:border-aura-cyan/50"
          aria-label="Open menu"
          data-testid="topbar-menu"
        >
          <Menu size={16} />
        </button>

        <div className="min-w-0 flex-1">
          <div className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-whisper-muted truncate">
            {subtitle || "AURA · dashboard"}
          </div>
          <h1 className="font-display text-[20px] sm:text-[22px] font-light tracking-tight text-ink truncate" data-testid="topbar-title">
            {title}
          </h1>
        </div>

        {/* Search trigger */}
        <button
          onClick={onOpenPalette}
          className="hidden md:flex items-center gap-2 px-3 py-2 rounded-full border border-panel/10 hover:border-aura-cyan/40 text-whisper-muted hover:text-ink transition-colors min-w-[260px]"
          data-testid="topbar-search"
        >
          <Search size={14} />
          <span className="font-mono text-[11px] tracking-[0.14em] uppercase">Search memory…</span>
          <span className="ml-auto kbd">⌘ K</span>
        </button>

        {/* Theme */}
        <button
          onClick={toggle}
          className="theme-swap"
          aria-label={`Switch to ${effective === "dark" ? "light" : "dark"} mode`}
          data-testid="topbar-theme-toggle"
        >
          <span className="knob grid place-items-center">
            {effective === "dark" ? <Moon size={10} className="text-ink" /> : <Sun size={10} className="text-ink" />}
          </span>
        </button>

        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => setOpenNotif((v) => !v)}
            className="w-9 h-9 grid place-items-center rounded-lg border border-panel/10 hover:border-aura-cyan/40 text-whisper relative"
            aria-label="Notifications"
            data-testid="topbar-notifications"
          >
            <Bell size={15} />
            <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-aura-cyan shadow-[0_0_8px_rgb(var(--accent-cyan-rgb)/0.9)]" />
          </button>
          {openNotif && (
            <div className="absolute right-0 mt-2 w-72 glass rounded-2xl p-3 z-40" data-testid="topbar-notifications-panel">
              <div className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-whisper-muted px-2 pb-2">Recent</div>
              {[
                "Weekly memory digest is ready.",
                "AURA noticed a fluency breakthrough.",
                "Streak — 12 days strong.",
              ].map((t, i) => (
                <div key={i} className="hairline rounded-xl px-3 py-2.5 text-[13.5px] text-ink/90 mb-1.5">
                  {t}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Profile */}
        <div className="relative">
          <button
            onClick={() => setOpenProfile((v) => !v)}
            className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border border-panel/10 hover:border-aura-cyan/40 transition-colors"
            data-testid="topbar-profile"
          >
            <span className="w-7 h-7 rounded-full bg-gradient-to-br from-aura-cyan/40 to-aura-violet/40 grid place-items-center font-display text-[13px] text-ink">{initial}</span>
            <ChevronDown size={12} className="text-whisper-muted" />
          </button>
          {openProfile && (
            <div className="absolute right-0 mt-2 w-56 glass rounded-2xl p-2 z-40" data-testid="topbar-profile-menu">
              <div className="px-3 py-2">
                <div className="font-mono text-[11px] tracking-[0.16em] uppercase text-ink">{name}</div>
                <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-whisper-muted">{cefr} · {lang}</div>
              </div>
              <a href="/dashboard/settings" className="block px-3 py-2 rounded-lg hover:bg-panel/[0.04] text-[13px] text-whisper hover:text-ink">Settings</a>
              <a href="/" className="block px-3 py-2 rounded-lg hover:bg-panel/[0.04] text-[13px] text-whisper hover:text-ink">Back to site</a>
              <button onClick={handleSignOut} className="w-full text-left block px-3 py-2 rounded-lg hover:bg-panel/[0.04] text-[13px] text-red-300">Sign out</button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default TopBar;
